/**
* DevExpress Dashboard (keyboard-navigation-extension.js)
* Version:  24.1.7
* Build date: Oct 29, 2024
* Copyright (c) 2012 - 2024 Developer Express Inc. ALL RIGHTS RESERVED
* License: https://www.devexpress.com/Support/EULAs/universal.xml
*/
import { DisposableObject } from '../../model';
import { defaultExtensions, extensionNameMap } from '../control-options';
const INTERACTIVITY_MODE_CLASS = 'dx-dashboard-item-container-activated';
const DASHBOARD_ITEM_SELECTOR = '.dx-dashboard-item-container';
const DASHBOARD_ITEM_FULLSCREEN_SELECTOR = '.dx-dashboard-fullscreen-item';
var KeyboardMode;
(function (KeyboardMode) {
    KeyboardMode["Interactive"] = "Interactive";
    KeyboardMode["Navigation"] = "Navigation";
})(KeyboardMode || (KeyboardMode = {}));
const _nameAlias = 'keyboard-navigation';
const name = 'keyboardNavigation';
const _dxDashboardTabsAttribute = 'dx-dashboard-layout-tab-index';
const _dxDashboardTabsEllipsisButton = '.dx-dashboard-tab-button.dx-dashboard-toggle-menu-button';
const _focusableElementSelector = 'button:enabled, input:enabled, select:enabled, textarea:enabled, iframe, object, summary, embed, audio[controls], video[controls], [autofocus], [contenteditable], [href], [tabindex]:not([tabindex="-1"]), [role="button"].dx-buttongroup-item-content:not(.dx-buttongroup-first-item), .dx-dashboard-toolbar-preview';
export class KeyboardNavigationExtension extends DisposableObject {
    constructor(_dashboardControl) {
        super();
        this._dashboardControl = _dashboardControl;
        this.name = 'keyboardNavigation';
        this._keyboardMode = KeyboardMode.Navigation;
        this._handleFocusIn = (event) => {
            const focusedDashboardItem = this._getActiveDashboardItem();
            focusedDashboardItem && (this._activeDashboardItemElement = focusedDashboardItem);
        };
        this._handleKeyDown = (event) => {
            switch (event.key) {
                case 'Tab':
                    this._handleTabKeyDown(event);
                    break;
                case 'Enter':
                    this._handleEnterKeyDown(event);
                    break;
                case 'Escape':
                    this._handleEscapeKeyDown(event);
                    break;
                case 'ArrowLeft':
                    this._handleArrowLeft(event);
                    break;
                case 'ArrowRight':
                    this._handleArrowRight(event);
                    break;
                default:
                    break;
            }
        };
        this._handleTabKeyDown = (event) => {
            let dashboardItems = Array.from(this._dashboardControl
                ._widgetContainer
                .querySelectorAll(`${DASHBOARD_ITEM_SELECTOR}:not(.dx-dashboard-hidden-element)[tabindex]:not([tabindex="-1"])`));
            const currentFocusedDashboardItem = this._getActiveDashboardItem();
            if (!currentFocusedDashboardItem)
                return;
            let index = dashboardItems.indexOf(currentFocusedDashboardItem);
            let fullScreenItems = Array.from(this._dashboardControl._widgetContainer.querySelectorAll(`${DASHBOARD_ITEM_FULLSCREEN_SELECTOR}`));
            if (fullScreenItems.length > 0) {
                const fullScreenItem = fullScreenItems[0].children[0];
                if (index != -1 && currentFocusedDashboardItem !== fullScreenItem) {
                    this._keyboardMode = KeyboardMode.Navigation;
                    this._focusElementRecursive(fullScreenItem);
                    event.preventDefault();
                    return;
                }
                dashboardItems = fullScreenItems.map(element => element.children[0]);
                index = 0;
            }
            if (dashboardItems.find(item => item === document.activeElement)) {
                this._keyboardMode = KeyboardMode.Navigation;
            }
            if (index === -1) {
                if (event.shiftKey) {
                    if (this._keyboardMode === KeyboardMode.Interactive && !this._isTargetPopup(event.target))
                        return;
                    this._focusElementRecursive(dashboardItems[dashboardItems.length - 1]);
                    this._keyboardMode = KeyboardMode.Navigation;
                    event.preventDefault();
                }
                if (!this._isTargetPopup(event.target))
                    return;
            }
            const childTabStopElements = this._getDashboardItemTabStopElements();
            if (this._keyboardMode === KeyboardMode.Interactive) {
                if (childTabStopElements.length === 0) {
                    event.preventDefault();
                    return;
                }
            }
            if (event.shiftKey) {
                if (this._keyboardMode === KeyboardMode.Navigation) {
                    if (index === 0) {
                        if (childTabStopElements.find(e => e === document.activeElement)) {
                            this._focusElementRecursive(dashboardItems[0]);
                            event.preventDefault();
                        }
                        return;
                    }
                    this._focusElementRecursive(dashboardItems[index - 1]);
                    event.preventDefault();
                }
                if (this._keyboardMode === KeyboardMode.Interactive) {
                    if (document.activeElement === childTabStopElements[0]) {
                        childTabStopElements[childTabStopElements.length - 1].focus();
                        event.preventDefault();
                    }
                }
            }
            if (!event.shiftKey) {
                if (this._keyboardMode === KeyboardMode.Navigation) {
                    if (index === dashboardItems.length - 1) {
                        if (childTabStopElements.length > 0) {
                            childTabStopElements[childTabStopElements.length - 1].focus();
                        }
                        return;
                    }
                    this._focusElementRecursive(dashboardItems[index + 1]);
                    event.preventDefault();
                }
                if (this._keyboardMode === KeyboardMode.Interactive) {
                    if (document.activeElement === childTabStopElements[childTabStopElements.length - 1]) {
                        childTabStopElements[0].focus();
                        event.preventDefault();
                    }
                }
            }
        };
        this._handleEnterKeyDown = (event) => {
            if (!event.target)
                return;
            if (!document.contains(event.target)) {
                this._activeDashboardItemElement?.focus();
                this._keyboardMode = KeyboardMode.Navigation;
            }
            const target = event.target;
            if (this._keyboardMode === KeyboardMode.Interactive) {
                if (!this._getParentElement(target, 'dx-dashboard-caption-toolbar')) {
                    target.click();
                }
                if (target.classList.contains('dx-dashboard-toolbar-preview')) {
                    target.dispatchEvent(new MouseEvent('mouseenter'));
                }
                return;
            }
            this._keyboardMode = KeyboardMode.Interactive;
            this._activeDashboardItemElement = this._getActiveDashboardItem();
            this._applyActiveStyles();
            let topButtonsTarget = this._activeDashboardItemElement;
            if (topButtonsTarget?.parentElement.classList.contains('dx-dashboard-tab-content')) {
                topButtonsTarget = this._getParentElement(this._activeDashboardItemElement, 'dx-dashboard-item-container');
                this._activeDashboardItemElement.click();
            }
            topButtonsTarget?.dispatchEvent(new MouseEvent('mouseover'));
            topButtonsTarget?.dispatchEvent(new MouseEvent('mouseenter'));
            const childTabStopElements = this._getDashboardItemTabStopElements();
            const floatingPanelToolBarButtonSelector = 'dx-button-has-icon';
            childTabStopElements.sort((a, b) => {
                if (a.classList.contains(floatingPanelToolBarButtonSelector) && !b.classList.contains(floatingPanelToolBarButtonSelector))
                    return -1;
                if (b.classList.contains(floatingPanelToolBarButtonSelector) && !a.classList.contains(floatingPanelToolBarButtonSelector))
                    return 1;
                return 0;
            });
            if (childTabStopElements.find(childElement => childElement === document.activeElement))
                return;
            event.preventDefault();
            childTabStopElements.length > 0 && childTabStopElements[0].focus();
        };
        this._handleEscapeKeyDown = (event) => {
            if (this._keyboardMode === KeyboardMode.Navigation)
                return;
            this._keyboardMode = KeyboardMode.Navigation;
            if (!this._activeDashboardItemElement)
                return;
            event.stopPropagation();
            this._activeDashboardItemElement.querySelector('.dx-dashboard-caption-toolbar.dx-toolbar')?.dispatchEvent(new MouseEvent('mouseleave'));
            this._activeDashboardItemElement.focus();
            this._activeDashboardItemElement.classList.remove(INTERACTIVITY_MODE_CLASS);
            this._activeDashboardItemElement.dispatchEvent(new MouseEvent('mouseout'));
            this._activeDashboardItemElement = null;
        };
        this._handleArrowLeft = (event) => {
            if (!event.target)
                return;
            const target = event.target;
            if (target.hasAttribute(_dxDashboardTabsAttribute) || target.parentElement?.querySelector(_dxDashboardTabsEllipsisButton)) {
                this._handleTabsArrowKeys(event.target, 'ArrowLeft');
                event.preventDefault();
            }
        };
        this._handleArrowRight = (event) => {
            if (!event.target)
                return;
            const target = event.target;
            if (target.hasAttribute(_dxDashboardTabsAttribute) || target.parentElement?.querySelector(_dxDashboardTabsEllipsisButton)) {
                this._handleTabsArrowKeys(event.target, 'ArrowRight');
                event.preventDefault();
            }
        };
        this._applyActiveStyles = () => {
            const activeElement = this._activeDashboardItemElement;
            if (!activeElement)
                return;
            const blurHandler = (event) => {
                activeElement.removeEventListener('blur', blurHandler);
                activeElement.classList.remove(INTERACTIVITY_MODE_CLASS);
            };
            activeElement.addEventListener('blur', blurHandler);
            activeElement.classList.add(INTERACTIVITY_MODE_CLASS);
        };
        this.toDispose(_dashboardControl.isDesignMode.subscribe(designMode => {
            if (designMode) {
                this.stop();
            }
            else {
                this.start();
            }
        }));
    }
    _isVisible(element) {
        return !!element.offsetParent;
    }
    _filterVisibleElements(elements) {
        return elements.filter(element => this._isVisible(element));
    }
    _getDashboardItemTabStopElements() {
        this._activeDashboardItemElement?.querySelectorAll('.dx-scrollable-container').forEach(element => element.setAttribute('tabindex', '-1'));
        let childTabStopElements = this._filterVisibleElements(Array.from(this._activeDashboardItemElement?.querySelectorAll(_focusableElementSelector) ?? []));
        childTabStopElements = childTabStopElements.filter(element => !element.classList.contains('dx-dashboard-item-container'));
        childTabStopElements.forEach(element => this._ensureTabIndex(element));
        return childTabStopElements;
    }
    _getActiveDashboardItem() {
        return document.activeElement.closest(DASHBOARD_ITEM_SELECTOR);
    }
    _getParentElement(element, className) {
        let currentElement = element;
        while (currentElement) {
            currentElement = currentElement.parentElement;
            if (currentElement?.classList.contains(className)) {
                return currentElement;
            }
        }
    }
    _focusElementRecursive(element) {
        if (element.hasAttribute('tabindex') && element.getAttribute('tabindex') === '0') {
            element.focus();
        }
        else {
            var childrenWithTabIndexZero = element.querySelectorAll('[tabindex="0"]');
            if (childrenWithTabIndexZero.length > 0)
                childrenWithTabIndexZero[0].focus();
        }
    }
    _ensureTabIndex(element) {
        if (!element)
            return false;
        if (!element.hasAttribute('tabindex') || element.getAttribute('tabindex') === '-1') {
            element.setAttribute('tabindex', '0');
        }
        return true;
    }
    _focusElement(element) {
        if (this._ensureTabIndex(element)) {
            element.focus();
        }
    }
    _handleTabsArrowKeys(element, key) {
        if (!element)
            return;
        let currentTabIndex = parseInt(element.getAttribute(_dxDashboardTabsAttribute));
        let tabContainer = element.parentElement;
        while (tabContainer && !tabContainer.classList.contains('dx-dashboard-layout-tabs-container')) {
            tabContainer = tabContainer.parentElement;
        }
        if (!tabContainer)
            return;
        const tabs = Array.from(tabContainer.querySelectorAll(`[${_dxDashboardTabsAttribute}]:not(.dx-dashboard-hidden-element), ${_dxDashboardTabsEllipsisButton}`));
        const totalTabsCount = tabContainer.querySelectorAll(`[${_dxDashboardTabsAttribute}]`)?.length ?? 0;
        currentTabIndex = tabs.findIndex(element => element.getAttribute(_dxDashboardTabsAttribute) === `${currentTabIndex}`);
        const tabsEllipsisButton = tabContainer.querySelector(_dxDashboardTabsEllipsisButton);
        if (tabsEllipsisButton) {
            tabsEllipsisButton.setAttribute(_dxDashboardTabsAttribute, `${totalTabsCount}`);
        }
        else {
            element.setAttribute('tabindex', '-1');
        }
        if (key === 'ArrowLeft') {
            currentTabIndex === 0 ? this._focusElement(tabs[tabs.length - 1]) : this._focusElement(tabs[currentTabIndex - 1]);
        }
        if (key === 'ArrowRight') {
            currentTabIndex >= tabs.length - 1 ? this._focusElement(tabs[0]) : this._focusElement(tabs[currentTabIndex + 1]);
        }
    }
    start() {
        if (this._dashboardControl?.isDesignMode())
            return;
        const subscribeToEvents = () => {
            this._dashboardControl._widgetContainer?.addEventListener('keydown', this._handleKeyDown);
            this._dashboardControl._widgetContainer?.addEventListener('focusin', this._handleFocusIn);
        };
        if (this._dashboardControl.dashboard.peek()) {
            subscribeToEvents();
        }
        else {
            const subscription = this._dashboardControl.dashboard.subscribe((newDashboard) => {
                subscription.dispose();
                subscribeToEvents();
            });
        }
    }
    stop() {
        this._dashboardControl._widgetContainer?.removeEventListener('keydown', this._handleKeyDown);
        this._dashboardControl._widgetContainer?.removeEventListener('focusin', this._handleFocusIn);
    }
    dispose() {
        this.stop();
        super.dispose();
        this._activeDashboardItemElement = null;
    }
    _isTargetPopup(element) {
        if (!element)
            return false;
        if (element.classList.contains('dx-overlay-content'))
            return true;
        return this._isTargetPopup(element.parentElement);
    }
}
defaultExtensions[_nameAlias] = (dashboardControl, options) => new KeyboardNavigationExtension(dashboardControl);
extensionNameMap[_nameAlias] = name;
